/** Certificates: record one from its document, and say what it will reprice - ADR-0226 (decision D-04).
 *
 * Until this drawer a REC or a REGO could be created only by owner SQL, so no customer's market-based
 * Scope 2 could ever be reduced by a certificate it bought (G-P4-01). It lives on Scope 2, where the
 * market-based figure is, and has three views of one act:
 *
 * 1. **The list**: the certificates held, what each covers today, and whether a reprice has been run
 *    for it. One table. An Approver or an Admin records one; everybody else is told who can
 *    (`certificate-who`), because a control that predictably 403s is UX-04's defect and a missing one
 *    with no reason is the one `formOrWhoCan` fails on (design rule 6).
 * 2. **The form**: the document it is recorded from (the extraction's reading pre-fills the boxes and
 *    the person confirms them), the kind, the vintage, the volume, where it applies and the five checks
 *    a certificate must meet. A certificate that fails a check cannot reduce a figure, so the form
 *    does not record one. The button is never greyed for a missing box: pressing it says what is
 *    missing, beside the box (`useFormChecks`, Q-06).
 * 3. **The confirmation**: the server runs the reprice and rolls it back, and this view says what it
 *    would move *before* anything is written (ADR-0087). The person chooses to reprice now or later.
 *    "Later" records the certificate and moves no figure; the list then offers Reprice.
 *
 * Nothing here computes a figure or a count: every number is the server's. A closed period is the
 * server's refusal, read through `PostCloseRetry`, which gives an Admin the way through and tells
 * anyone else whose act it is.
 */

import { useState } from "react";

import { useFacilities } from "../../api/evidence";
import { useReaderNow } from "../../api/profile";
import { isPeriodClosed } from "../../api/refusal";
import {
  useInstruments,
  usePreviewInstrument,
  useRecordInstrument,
  useRepriceInstrument,
} from "../../api/instruments";
import type { CertificateDocument, InstrumentInput, InstrumentPreview, InstrumentRow } from "../../api/instruments";
import { FieldError } from "../../components/FieldError";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { DataTable, Drawer, InfoPopover } from "../../components/ui";
import type { Column } from "../../components/ui";
import { useFormChecks } from "../../components/useFormChecks";
import { readDecimal } from "../../lib/decimalInput";
import { quantity } from "../../lib/format";
import { PostCloseRetry } from "../PeriodLock";
import styles from "./scopeTable.module.css";
import own from "./certificates.module.css";

type View = "list" | "form" | "confirm";

/** The five checks, in the order the Scope 2 Quality Criteria print them (1 to 5), in plain words.
 * The keys are the API's and the engine's own (`Instrument.CRITERIA_ALL`). */
const CHECKS: ReadonlyArray<{ key: keyof ChecksState; label: string }> = [
  { key: "attributes", label: "It carries the emission-rate attribute of the electricity it covers." },
  { key: "exclusive", label: "No other instrument claims the same electricity." },
  { key: "retired", label: "It was retired by or for the company." },
  { key: "vintage", label: "It is for the period of the consumption it is applied to." },
  { key: "market", label: "It is from the same market as the site." },
];

interface ChecksState {
  attributes: boolean;
  exclusive: boolean;
  retired: boolean;
  vintage: boolean;
  market: boolean;
}

const NO_CHECKS: ChecksState = {
  attributes: false,
  exclusive: false,
  retired: false,
  vintage: false,
  market: false,
};

/** The API's field names for this form's, so a refusal lands beside its own box. */
const SERVER_FIELD: Readonly<Record<string, string>> = {
  document_id: "document",
  kind: "kind",
  vintage_year: "vintage",
  mwh: "mwh",
  facility_id: "site",
  certificate_reference: "reference",
  registry_name: "registry",
  retired_at: "retired",
  criteria: "checks",
  checks: "checks",
};

const FIVE_CHECKS_SENTENCE =
  "Confirm each of the five checks, or do not record this certificate: one that fails a check cannot reduce a figure.";

const figures = (count: number) => `${quantity(String(count))} figure${count === 1 ? "" : "s"}`;

/** A volume as the registry prints it: "40", not "40.000000", and "12.5" stays "12.5". */
function plain(value: string): string {
  const trimmed = value.includes(".") ? value.replace(/0+$/, "").replace(/\.$/, "") : value;
  const places = trimmed.includes(".") ? (trimmed.split(".")[1] ?? "").length : 0;
  return quantity(trimmed, places);
}

interface Done {
  recorded: boolean;
  repriced: boolean;
  changed: number;
}

export function CertificatesDrawer({
  open,
  onClose,
  role,
}: {
  open: boolean;
  onClose: () => void;
  role: string | undefined;
}) {
  const canRecord = role === "admin" || role === "approver";
  const read = useInstruments(open);
  const preview = usePreviewInstrument();
  const record = useRecordInstrument();
  const reprice = useRepriceInstrument();
  const readerNow = useReaderNow();
  const [checks, attach] = useFormChecks();

  const [view, setView] = useState<View>("list");
  const facilities = useFacilities(open && canRecord && view !== "list");

  const [documentId, setDocumentId] = useState("");
  const [kind, setKind] = useState("");
  const [vintage, setVintage] = useState("");
  const [mwh, setMwh] = useState("");
  const [site, setSite] = useState("");
  const [registry, setRegistry] = useState("");
  const [reference, setReference] = useState("");
  const [retired, setRetired] = useState("");
  const [attested, setAttested] = useState<ChecksState>(NO_CHECKS);

  const [measured, setMeasured] = useState<InstrumentPreview | null>(null);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [repriceId, setRepriceId] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  const documents: readonly CertificateDocument[] = read.data?.documents ?? [];

  function reset() {
    setView("list");
    setDocumentId("");
    setKind("");
    setVintage("");
    setMwh("");
    setSite("");
    setRegistry("");
    setReference("");
    setRetired("");
    setAttested(NO_CHECKS);
    setMeasured(null);
    setReason(undefined);
    setRefusal(null);
    checks.clear();
  }

  function close() {
    reset();
    setDone(null);
    setRepriceId(null);
    onClose();
  }

  function choose(id: string) {
    checks.touch("document");
    setDocumentId(id);
    const picked = documents.find((candidate) => candidate.id === id);
    // What the extraction read is a suggestion the person confirms, never a value they did not see.
    if (picked) {
      setMwh(picked.volume_mwh ?? "");
      setVintage(picked.vintage_year ?? "");
      setRegistry(picked.registry ?? "");
      setReference(picked.certificate_reference ?? "");
    }
  }

  function rules(volume: string) {
    const year = Number(vintage);
    const parsed = readDecimal(mwh, readerNow()?.locale);
    return [
      {
        field: "document",
        ok: documentId !== "",
        message:
          documents.length > 0
            ? "Choose the certificate document this is recorded from."
            : "There is no certificate document to record from yet.",
      },
      { field: "kind", ok: kind !== "", message: "Say whether it is a REC or a REGO." },
      {
        field: "vintage",
        ok: /^\d{4}$/.test(vintage.trim()) && year >= 2000 && year <= 2100,
        message: "Give the vintage as a four-digit year, such as 2026.",
      },
      {
        field: "mwh",
        ok: volume !== "" && !volume.startsWith("-") && /[1-9]/.test(volume),
        message: parsed.ok ? "Type the volume in MWh, as a number such as 40 or 12.5." : parsed.sentence,
      },
      { field: "site", ok: site !== "", message: "Say which site it applies to, or every site." },
      {
        field: "reference",
        ok: reference.trim() !== "",
        message: "Give the certificate reference: it is how an assurer finds it in the registry.",
      },
      { field: "retired", ok: retired !== "", message: "Choose the day it was retired." },
      {
        field: "checks",
        ok: Object.values(attested).every(Boolean),
        message: FIVE_CHECKS_SENTENCE,
      },
    ];
  }

  function body(when: "now" | "later", postCloseReason: string | undefined): InstrumentInput {
    const volume = readDecimal(mwh, readerNow()?.locale);
    return {
      kind: kind as "rec" | "rego",
      vintage_year: Number(vintage.trim()),
      // Exact (rule 5): a string the server reads as a Decimal, never a number.
      mwh: volume.ok ? volume.value : "",
      facility_id: site === "all" ? null : site,
      document_id: documentId,
      certificate_reference: reference.trim(),
      ...(registry.trim() ? { registry_name: registry.trim() } : {}),
      retired_at: retired,
      checks: attested,
      reprice: when,
      ...(postCloseReason ? { post_close_reason: postCloseReason } : {}),
    };
  }

  /** Step one: ask the server what recording would move. Nothing is written. */
  async function review(postCloseReason?: string) {
    setRefusal(null);
    setDone(null);
    const volume = readDecimal(mwh, readerNow()?.locale);
    if (!checks.run(rules(volume.ok ? volume.value : ""))) return;
    if (postCloseReason) setReason(postCloseReason);
    try {
      setMeasured(await preview.mutateAsync(body("now", postCloseReason ?? reason)));
      setView("confirm");
    } catch (error) {
      setView("form");
      if (!checks.refuse(error, SERVER_FIELD)) setRefusal(error);
    }
  }

  /** Step two: record it, repricing now or leaving that for later. */
  async function submit(when: "now" | "later") {
    setRefusal(null);
    try {
      const result = await record.mutateAsync(body(when, reason));
      setDone({ recorded: true, repriced: result.repriced, changed: result.figures_changed });
      reset();
    } catch (error) {
      // A refusal about a box goes back to the form, beside the box; a closed period goes back to it
      // too, where an Admin can give the reason; anything else is said here, where the person is.
      if (checks.refuse(error, SERVER_FIELD)) {
        setView("form");
        return;
      }
      if (isPeriodClosed(error)) setView("form");
      setRefusal(error);
    }
  }

  async function repriceOne(id: string, postCloseReason?: string) {
    setRefusal(null);
    setDone(null);
    setRepriceId(id);
    try {
      const result = await reprice.mutateAsync({
        id,
        ...(postCloseReason ? { postCloseReason } : {}),
      });
      setDone({ recorded: false, repriced: true, changed: result.figures_changed });
      setRepriceId(null);
    } catch (error) {
      setRefusal(error);
    }
  }

  const rows: readonly InstrumentRow[] = read.data?.instruments ?? [];
  const columns: readonly Column<InstrumentRow>[] = [
    {
      id: "certificate",
      header: "Certificate",
      cell: (row) => row.name ?? `${row.kind.toUpperCase()} ${String(row.vintage_year)}`,
    },
    { id: "site", header: "Site", cell: (row) => row.site_name ?? "Every site" },
    {
      id: "volume",
      header: "Volume",
      align: "right",
      cell: (row) => `${plain(row.mwh)} MWh`,
    },
    {
      id: "covers",
      header: "Covers",
      align: "right",
      cell: (row) => `${quantity(row.drawn_kwh)} of ${quantity(row.capacity_kwh)} kWh`,
    },
    {
      id: "applied",
      header: "Figures",
      cell: (row) => (
        <span className={own.status}>
          {row.applied ? "Repriced" : "Not repriced yet"}
          {canRecord && !row.applied ? (
            <button
              type="button"
              className={own.small}
              disabled={reprice.isPending}
              data-testid={`certificate-reprice-${row.id}`}
              onClick={() => void repriceOne(row.id)}
            >
              {reprice.isPending && repriceId === row.id ? "Repricing…" : "Reprice"}
            </button>
          ) : null}
        </span>
      ),
    },
  ];

  const sentence = done ? (
    <p className={styles.done} role="status" data-testid="certificate-done">
      <strong>{done.recorded ? "Recorded." : "Repriced."}</strong>{" "}
      {done.repriced
        ? done.changed > 0
          ? `${figures(done.changed)} repriced. The old receipts still open and say they were replaced.`
          : "No figure changed."
        : "No figure has changed yet. Reprice it from the list when you are ready."}
    </p>
  ) : null;

  const footer =
    view === "form" ? (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          disabled={preview.isPending}
          data-testid="certificate-review"
          onClick={() => void review()}
        >
          {preview.isPending ? "Checking…" : "Review"}
        </button>
        <button type="button" className={styles.ghost} data-testid="certificate-cancel" onClick={reset}>
          Cancel
        </button>
      </div>
    ) : view === "confirm" ? (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          disabled={record.isPending}
          data-testid="certificate-record-now"
          onClick={() => void submit("now")}
        >
          {record.isPending ? "Recording…" : "Record and reprice now"}
        </button>
        <button
          type="button"
          className={styles.ghost}
          disabled={record.isPending}
          data-testid="certificate-record-later"
          onClick={() => void submit("later")}
        >
          Record, reprice later
        </button>
        <button type="button" className={styles.ghost} data-testid="certificate-back" onClick={() => setView("form")}>
          Back
        </button>
      </div>
    ) : (
      <div className={styles.actions}>
        {canRecord ? (
          <button
            type="button"
            className={styles.primary}
            data-testid="record-certificate"
            onClick={() => {
              setDone(null);
              setRefusal(null);
              setView("form");
            }}
          >
            Record a certificate
          </button>
        ) : null}
        <button type="button" className={styles.ghost} data-testid="certificates-close" onClick={close}>
          Close
        </button>
        <InfoPopover infoKey="scope2.certificates" />
      </div>
    );

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Certificates"
      width="md"
      testId="certificates-drawer"
      footer={footer}
    >
      <div className={own.body}>
        {view === "list" ? (
          <>
            {sentence}
            {!canRecord ? (
              <p className={own.who} data-testid="certificate-who">
                An Approver or an Admin records a certificate.
              </p>
            ) : null}
            {read.error ? (
              <ReadFailed of="The certificates" error={read.error} onRetry={() => void read.refetch()} />
            ) : (
              <DataTable
                caption="Certificates"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                loading={read.isLoading}
                empty={<p className={own.who}>No certificate is recorded yet.</p>}
                testId="certificates-table"
              />
            )}
            <PostCloseRetry
              error={refusal}
              pending={reprice.isPending}
              {...(repriceId ? { onRecord: (why: string) => void repriceOne(repriceId, why) } : {})}
            />
            <WriteFailed error={isPeriodClosed(refusal) ? null : refusal} of="the reprice" />
          </>
        ) : null}

        {view !== "list" ? (
          <form
            ref={attach}
            className={styles.form}
            noValidate
            hidden={view === "confirm"}
            onSubmit={(event) => {
              event.preventDefault();
              if (!preview.isPending) void review();
            }}
          >
            {read.error ? (
              <ReadFailed of="The certificate documents" error={read.error} onRetry={() => void read.refetch()} />
            ) : (
              <label className={styles.field}>
                Certificate document
                <select
                  aria-label="Which certificate document?"
                  {...checks.field("document")}
                  value={documentId}
                  onChange={(event) => choose(event.target.value)}
                >
                  <option value="">
                    {documents.length > 0 ? "Choose one" : "None waiting. Upload and read one first"}
                  </option>
                  {documents.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.filename ?? "Certificate"}
                    </option>
                  ))}
                </select>
                <FieldError {...checks.say("document")} />
              </label>
            )}

            <div className={styles.pair}>
              <label className={styles.field}>
                Kind
                <select
                  aria-label="REC or REGO?"
                  {...checks.field("kind")}
                  value={kind}
                  onChange={(event) => {
                    checks.touch("kind");
                    setKind(event.target.value);
                  }}
                >
                  <option value="">Choose one</option>
                  <option value="rec">REC</option>
                  <option value="rego">REGO</option>
                </select>
                <FieldError {...checks.say("kind")} />
              </label>
              <label className={styles.field}>
                Vintage year
                <input
                  aria-label="Vintage year"
                  inputMode="numeric"
                  {...checks.field("vintage")}
                  value={vintage}
                  onChange={(event) => {
                    checks.touch("vintage");
                    setVintage(event.target.value);
                  }}
                />
                <FieldError {...checks.say("vintage")} />
              </label>
            </div>

            <div className={styles.pair}>
              <label className={styles.field}>
                Volume (MWh)
                <input
                  aria-label="Volume in MWh"
                  inputMode="decimal"
                  {...checks.field("mwh")}
                  value={mwh}
                  onChange={(event) => {
                    checks.touch("mwh");
                    setMwh(event.target.value);
                  }}
                />
                <FieldError {...checks.say("mwh")} />
              </label>
              <label className={styles.field}>
                Applies to
                {facilities.error ? (
                  <ReadFailed
                    of="The list of sites"
                    error={facilities.error}
                    onRetry={() => void facilities.refetch()}
                  />
                ) : (
                  <select
                    aria-label="Which site?"
                    {...checks.field("site")}
                    value={site}
                    onChange={(event) => {
                      checks.touch("site");
                      setSite(event.target.value);
                    }}
                  >
                    <option value="">Choose one</option>
                    <option value="all">Every site</option>
                    {(facilities.data ?? []).map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                )}
                <FieldError {...checks.say("site")} />
              </label>
            </div>

            <div className={styles.pair}>
              <label className={styles.field}>
                Certificate reference
                <input
                  aria-label="Certificate reference"
                  {...checks.field("reference")}
                  value={reference}
                  onChange={(event) => {
                    checks.touch("reference");
                    setReference(event.target.value);
                  }}
                />
                <FieldError {...checks.say("reference")} />
              </label>
              <label className={styles.field}>
                Registry (optional)
                <input
                  aria-label="Registry"
                  {...checks.field("registry")}
                  value={registry}
                  onChange={(event) => {
                    checks.touch("registry");
                    setRegistry(event.target.value);
                  }}
                />
                <FieldError {...checks.say("registry")} />
              </label>
            </div>

            <label className={styles.field}>
              Retired on
              <input
                aria-label="Retired on"
                type="date"
                {...checks.field("retired")}
                value={retired}
                onChange={(event) => {
                  checks.touch("retired");
                  setRetired(event.target.value);
                }}
              />
              <FieldError {...checks.say("retired")} />
            </label>

            <fieldset
              className={own.checks}
              tabIndex={-1}
              {...checks.field("checks")}
              data-testid="certificate-checks"
            >
              <legend>Confirm the certificate</legend>
              {CHECKS.map((check) => (
                <label key={check.key} className={own.check}>
                  <input
                    type="checkbox"
                    checked={attested[check.key]}
                    data-testid={`certificate-check-${check.key}`}
                    onChange={(event) => {
                      checks.touch("checks");
                      setAttested((current) => ({ ...current, [check.key]: event.target.checked }));
                    }}
                  />
                  {check.label}
                </label>
              ))}
              <FieldError {...checks.say("checks")} />
            </fieldset>

            <PostCloseRetry
              error={refusal}
              pending={preview.isPending}
              onRecord={(why) => void review(why)}
            />
            <WriteFailed error={isPeriodClosed(refusal) ? null : refusal} of="the certificate" />
          </form>
        ) : null}

        {view === "confirm" && measured ? (
          <div className={own.body} data-testid="certificate-confirm">
            <p className={own.summary} role="status" data-testid="certificate-will-move">
              {measured.figures_changed > 0
                ? `Recording this will reprice ${figures(measured.figures_changed)} in ${measured.window_start.slice(0, 4)}.`
                : `No figure in ${measured.window_start.slice(0, 4)} will change.`}
            </p>
            <WriteFailed error={isPeriodClosed(refusal) ? null : refusal} of="the certificate" />
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
